import { useInView } from "@/hooks/useInView";
import { Tractor, Building2, Trees, ArrowRight } from "lucide-react";

const scrollToForm = () => {
  document.getElementById("hero-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const personas = [
  {
    icon: Tractor,
    badge: "Rolnik / gospodarz",
    title: "Masz ziemię rolną przy drodze",
    description: "Pole orne lub łąka, najlepiej w pobliżu drogi krajowej, wojewódzkiej lub powiatowej. Idealne lokalizacje to obrzeża wsi, granice gmin, działki przy skrzyżowaniach.",
    bullets: [
      "Wystarczy ok. 12 × 12 m pod sam maszt 65 m",
      "Pozostała ziemia nadal może być uprawiana",
      "Stały roczny czynsz niezależny od cen skupu i pogody",
    ],
    targetLabel: "Wystarczająca powierzchnia",
    targetValue: "ok. 150 m²",
    cta: "Sprawdź potencjał gruntu",
    color: "emerald",
  },
  {
    icon: Building2,
    badge: "Właściciel hali / dachu",
    title: "Masz dach magazynu lub hali",
    description: "Hala produkcyjna, magazyn, obiekt logistyczny — szczególnie ten w strefie przemysłowej lub przy drodze tranzytowej. Maszt na dachu nie utrudnia eksploatacji budynku.",
    bullets: [
      "Maszt antenowy 3–6 m, bez uciążliwości operacyjnych",
      "Dodatkowy stały dochód niezależny od działalności firmy",
      "Brak zmian w strukturze nośnej (ekspertyza po naszej stronie)",
    ],
    targetLabel: "Wystarczy",
    targetValue: "kilka m² dachu",
    cta: "Sprawdź potencjał dachu",
    color: "amber",
  },
  {
    icon: Trees,
    badge: "Inwestor / spadkobierca",
    title: "Masz nieużywaną działkę",
    description: "Działka odziedziczona, leżąca odłogiem, w obrębie zabudowy lub strefie inwestycyjnej. Jeśli nie masz czasu jej zagospodarować, dzierżawa pod maszt to bezobsługowe rozwiązanie.",
    bullets: [
      "Zero zaangażowania w obsługę po podpisaniu umowy",
      "Umowa „chodzi z gruntem” — można sprzedać działkę z czynszem",
      "Realny wzrost wartości nieruchomości dla rzeczoznawców",
    ],
    targetLabel: "Lokalizacja",
    targetValue: "obrzeża miast",
    cta: "Sprawdź potencjał inwestycji",
    color: "slate",
  },
];

export default function ForWhom() {
  const [ref, inView] = useInView(0.15);

  return (
    <section
      id="dla-kogo"
      className="py-24 bg-white"
      data-testid="for-whom-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-emerald-700 text-sm font-semibold uppercase tracking-widest">
            Dla kogo
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight">
            Z którą sytuacją utożsamiasz się najbardziej?
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            Każda lokalizacja jest inna. Sprawdź, do której grupy należysz — to pomoże nam szybciej
            dopasować ofertę i ocenić potencjał Twojej nieruchomości.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {personas.map((p, i) => (
            <PersonaCard key={p.badge} persona={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function PersonaCard({ persona, index }) {
  const [ref, inView] = useInView();
  const Icon = persona.icon;

  const styles = {
    emerald: {
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-700",
      badgeText: "text-emerald-700",
      checkBg: "bg-emerald-100",
      checkColor: "text-emerald-700",
      target: "bg-emerald-50 border-emerald-200 text-emerald-900",
      targetValue: "text-emerald-700",
      button: "bg-emerald-700 hover:bg-emerald-800 text-white",
    },
    amber: {
      iconBg: "bg-amber-100",
      iconColor: "text-amber-700",
      badgeText: "text-amber-700",
      checkBg: "bg-amber-100",
      checkColor: "text-amber-700",
      target: "bg-amber-50 border-amber-200 text-amber-900",
      targetValue: "text-amber-700",
      button: "bg-amber-500 hover:bg-amber-600 text-white",
    },
    slate: {
      iconBg: "bg-slate-100",
      iconColor: "text-slate-700",
      badgeText: "text-slate-600",
      checkBg: "bg-slate-100",
      checkColor: "text-slate-700",
      target: "bg-slate-50 border-slate-200 text-slate-900",
      targetValue: "text-slate-900",
      button: "bg-slate-900 hover:bg-slate-800 text-white",
    },
  }[persona.color];

  return (
    <div
      ref={ref}
      className={`bg-slate-50 rounded-3xl p-7 border border-slate-200 hover:shadow-xl hover:shadow-emerald-900/5 hover:-translate-y-1 transition-all duration-700 flex flex-col h-full ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
      data-testid={`persona-card-${index + 1}`}
    >
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-12 h-12 ${styles.iconBg} rounded-xl flex items-center justify-center`}>
          <Icon className={`w-6 h-6 ${styles.iconColor}`} aria-hidden="true" />
        </div>
        <span className={`text-xs font-bold uppercase tracking-widest ${styles.badgeText}`}>
          {persona.badge}
        </span>
      </div>

      <h3 className="text-xl font-black text-slate-900 tracking-tight mb-3">
        {persona.title}
      </h3>
      <p className="text-slate-600 text-sm leading-relaxed mb-5">
        {persona.description}
      </p>

      <ul className="space-y-2.5 mb-6 flex-1">
        {persona.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-slate-700 text-sm">
            <div className={`w-5 h-5 ${styles.checkBg} rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`}>
              <svg className={`w-3 h-3 ${styles.checkColor}`} viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border mb-5 ${styles.target}`}>
        <span className="text-xs font-medium opacity-80">{persona.targetLabel}</span>
        <span className={`text-sm font-black tabular-nums ${styles.targetValue}`}>{persona.targetValue}</span>
      </div>

      <button
        type="button"
        onClick={scrollToForm}
        className={`w-full font-semibold py-3 rounded-full transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${styles.button}`}
      >
        <span>{persona.cta}</span>
        <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
